import type {
  AddressInput,
  AddressMediaView,
  AddressView,
  ApiSuccess,
  AreaVoteResult,
  CustomerProfile,
  MediaUploadInput,
  MediaUploadTicket,
  UpdateAddressInput,
  UpdateProfileInput,
} from '@capsico/contracts';
import axios from 'axios';
import { api } from './client';
import { turnstileHeaders } from './turnstile';

/* Profile */

export async function fetchProfile(): Promise<CustomerProfile> {
  const res = await api.get<ApiSuccess<CustomerProfile>>('/v1/customer/me/profile');
  return res.data.data;
}

export async function saveProfile(input: UpdateProfileInput): Promise<CustomerProfile> {
  const res = await api.put<ApiSuccess<CustomerProfile>>('/v1/customer/me/profile', input);
  return res.data.data;
}

/* Address media: ask for a short-lived upload link, PUT the file straight to storage, confirm. */

export async function uploadAddressMedia(
  input: MediaUploadInput,
  file: Blob,
  onProgress?: (percent: number) => void,
): Promise<AddressMediaView> {
  const ticket = await api.post<ApiSuccess<MediaUploadTicket>>(
    '/v1/customer/me/address-media',
    input,
  );
  const { mediaId, url, headers } = ticket.data.data;
  // Plain axios (not the API client): the upload goes to storage, not to the Capsico API.
  await axios.put(url, file, {
    headers,
    timeout: 120_000,
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
  const done = await api.post<ApiSuccess<AddressMediaView>>(
    `/v1/customer/me/address-media/${mediaId}/complete`,
  );
  return done.data.data;
}

/* Saved addresses */

export async function fetchAddresses(): Promise<AddressView[]> {
  const res = await api.get<ApiSuccess<AddressView[]>>('/v1/customer/me/addresses');
  return res.data.data;
}

export async function createAddress(input: AddressInput): Promise<AddressView> {
  const res = await api.post<ApiSuccess<AddressView>>('/v1/customer/me/addresses', input);
  return res.data.data;
}

export async function updateAddress(id: string, input: UpdateAddressInput): Promise<AddressView> {
  const res = await api.put<ApiSuccess<AddressView>>(`/v1/customer/me/addresses/${id}`, input);
  return res.data.data;
}

export async function deleteAddress(id: string): Promise<void> {
  await api.delete(`/v1/customer/me/addresses/${id}`);
}

export async function setDefaultAddress(id: string): Promise<AddressView[]> {
  const res = await api.post<ApiSuccess<AddressView[]>>(`/v1/customer/me/addresses/${id}/default`);
  return res.data.data;
}

/* "Bring Capsico here" votes (guests too) */

/** W25: `turnstileToken` is sent as x-turnstile-token in the browser build (none in the native app). */
export async function voteForArea(
  input: {
    lat: number;
    lng: number;
    deviceId: string;
    notifyPhone?: string;
  },
  turnstileToken?: string | null,
): Promise<AreaVoteResult> {
  const res = await api.post<ApiSuccess<AreaVoteResult>>('/v1/customer/area-votes', input, {
    headers: turnstileHeaders(turnstileToken),
  });
  return res.data.data;
}

export async function fetchAreaVotes(point: {
  lat: number;
  lng: number;
}): Promise<{ votesNearby: number }> {
  const res = await api.get<ApiSuccess<{ votesNearby: number }>>('/v1/customer/area-votes', {
    params: point,
  });
  return res.data.data;
}
